import {
  type RefCallback,
  useCallback,
  useEffect,
  useLayoutEffect,
  useState
} from "react"

export interface ElementSize {
  width: number
  height: number
}

/**
 * An element's own rendered content-box size, read via ResizeObserver.
 *
 * `initial` is what callers see until a real measurement lands, and stays in
 * effect whenever the measured rect is exactly 0×0 — jsdom's `getBoundingClientRect`
 * never computes real layout and always reports zero (see `setupTests.ts`'s
 * no-op `ResizeObserver` stub, whose callback never fires either), so this is
 * also what every unit test sees for the life of the render. That makes
 * `initial` the deliberate seam for a duration-based estimate in a grid whose
 * real box size a test can't otherwise see (`estimateEventHeightPx`).
 *
 * Never read once per render: mid-view-switch a calendar card's box honestly
 * reads as the old view's column width and then the new one's, so a per-render
 * reading schedules the render that takes the next one until React's
 * nested-update limit throws.
 *
 * Held in state, not a ref, so the reading and the observer follow the element
 * rather than whatever a ref happened to hold when they last ran: a caller may
 * render the measured element on only some of its return paths.
 */
export function useElementSize<T extends HTMLElement = HTMLDivElement>(
  initial: ElementSize
): { ref: RefCallback<T>; size: ElementSize } {
  const [node, setNode] = useState<T | null>(null)
  const [size, setSize] = useState<ElementSize>(initial)

  const measure = useCallback((target: T) => {
    const rect = target.getBoundingClientRect()
    if (rect.width === 0 && rect.height === 0) return

    setSize((current) =>
      current.width === rect.width && current.height === rect.height
        ? current
        : { width: rect.width, height: rect.height }
    )
  }, [])

  useLayoutEffect(() => {
    if (node) measure(node)
  }, [node, measure])

  useEffect(() => {
    if (!node || typeof ResizeObserver === "undefined") return undefined

    const observer = new ResizeObserver(() => measure(node))
    observer.observe(node)
    return () => observer.disconnect()
  }, [node, measure])

  return { ref: setNode, size }
}
